import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import {
  companySettingByIdentifierSelector,
  waypointColorSelector,
} from "@store/settings/settings-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { updateCompanySetting } from "@store/settings/settings-thunks";
import { useCallback, useMemo, useState } from "react";
import { useCompanyId } from "@context-providers/company-id-provider";
import { WebEditorUtils } from "@stellar/utils";

interface UseWaypointColorReturn {
  /** The current waypoint color (hex string) */
  waypointColor: string;

  /** Updates the waypoint color via the Settings API */
  updateWaypointColor: (color: string) => Promise<void>;

  /** Whether the setting is being saved */
  isLoading: boolean;
}

/**
 * Hook that reads and updates the user's default waypoint color setting.
 * The current value is read from the Redux store (populated by the initial
 * batch fetch in UserLoader) and updates are persisted via the Settings API.
 */
export function useWaypointColor(): UseWaypointColorReturn {
  const { handleErrorWithToast } = useErrorContext();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const companyId = useCompanyId();

  const waypointColor = useAppSelector(waypointColorSelector);
  const waypointSettingSelector = useMemo(
    () =>
      companySettingByIdentifierSelector(companyId, "waypoints_custom_color"),
    [companyId]
  );
  const waypointSetting = useAppSelector(waypointSettingSelector);

  const updateWaypointColor = useCallback(
    async (color: string): Promise<void> => {
      if (!WebEditorUtils.isValidHexColor(color)) {
        return;
      }

      try {
        setIsLoading(true);
        await dispatch(
          updateCompanySetting({
            identifier: "waypoints_custom_color",
            companyId,
            payload: {
              value: color,
              modifiedAt: waypointSetting?.modifiedAt ?? "",
            },
          })
        ).unwrap();
      } catch (error) {
        handleErrorWithToast({
          id: `update-waypoint-color-${Date.now().toString()}`,
          title: "Failed to update the default waypoint color.",
          error,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [companyId, dispatch, handleErrorWithToast, waypointSetting]
  );

  return { waypointColor, updateWaypointColor, isLoading };
}
